import { Link } from 'react-router-dom';
import { MessageCircle, Shield, Key, Lock, FileCheck, Eye, Users, RefreshCw } from 'lucide-react';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

const SecurityCard = ({ icon: Icon, title, items }) => (
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center">
        <Icon size={24} className="text-white" />
      </div>
      <h3 className="text-xl font-bold text-gray-900">{title}</h3>
    </div>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-gray-600">
          <span className="text-green-500 mt-1">✓</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function SecurityCompliancePage() {
  return (
    <div data-testid="security-compliance-page">
      <Navbar />
      <SEOHead title="Security & Compliance" description="ChannelLoyalty security and compliance standards. Enterprise-grade data protection, TDS compliance, KYC verification, and audit trails for B2B loyalty programs." canonical="https://channelloyalty.ai/security-compliance" />
      
      <main>
        {/* Hero */}
        <section className="hero-full" style={{ paddingBottom: '60px' }}>
          <div className="container">
            <div className="text-center pt-8 max-w-4xl mx-auto">
              <div className="hero-badge mx-auto mb-6">
                <Shield size={16} />
                Security & Compliance
              </div>
              <h1 className="text-white mb-6">Security First. Compliance Built-In.</h1>
              <p className="text-white/80 text-lg">
                At ChannelLoyalty, security and compliance are not add-ons—they are foundational. Our platform is designed to protect user data, ensure regulatory adherence, and deliver secure reward experiences at scale.
              </p>
            </div>
          </div>
        </section>

        {/* Security Features */}
        <section className="section">
          <div className="container">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <SecurityCard
                icon={Lock}
                title="Data Security & Privacy"
                items={[
                  "End-to-end encryption for data in transit (HTTPS / TLS)",
                  "Secure data storage with role-based access controls",
                  "Regular security audits and vulnerability assessments",
                  "Strict internal access policies and monitoring"
                ]}
              />
              <SecurityCard
                icon={Key}
                title="KYC & Identity Verification"
                items={[
                  "PAN and Aadhaar-based verification (where applicable)",
                  "Secure API-based identity checks",
                  "Name, DOB, and address validation to prevent fraud",
                  "Duplicate and misuse detection mechanisms"
                ]}
              />
              <SecurityCard
                icon={FileCheck}
                title="Regulatory Compliance"
                items={[
                  "IT Act & data protection best practices",
                  "RBI-compliant payout workflows (UPI, vouchers, wallets)",
                  "Audit-ready transaction logs and reports",
                  "Consent-based data collection and usage"
                ]}
              />
              <SecurityCard
                icon={Shield}
                title="Secure Rewards & Payouts"
                items={[
                  "Verified UPI transfers",
                  "Authorized e-gift voucher partners",
                  "Controlled physical gift dispatch workflows",
                  "Fraud checks before every redemption"
                ]}
              />
              <SecurityCard
                icon={Eye}
                title="Transparency & Auditability"
                items={[
                  "QR scan validation logs",
                  "Earn and redemption transaction history",
                  "User-level and program-level reports",
                  "Exportable audit data for finance and compliance teams"
                ]}
              />
              <SecurityCard
                icon={Users}
                title="Partner & Vendor Security"
                items={[
                  "Vetted technology and fulfillment partners",
                  "Security standards compliance required",
                  "End-to-end trust across the ecosystem",
                  "Regular partner security assessments"
                ]}
              />
            </div>
          </div>
        </section>

        {/* Continuous Improvement */}
        <section className="section-light section">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center mx-auto mb-6">
                <RefreshCw size={32} className="text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Continuous Improvement</h2>
              <p className="text-gray-600 text-lg mb-8">
                Security is an ongoing process. We continuously evolve our platform to address emerging threats, regulatory updates, and enterprise requirements.
              </p>
              <p className="text-gray-600">
                User data is collected only for program execution and is never sold or shared with unauthorized third parties.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="section-dark section">
          <div className="container">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-4">Questions or Compliance Requests?</h2>
              <p className="text-gray-400 mb-8">
                For security documentation, audits, or compliance queries, reach out to us.
              </p>
              <a href="mailto:chandra@channelloyalty.ai" className="btn btn-primary">
                Contact Us at chandra@channelloyalty.ai
              </a>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}
